import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

/**
 * Modern, theme-aware loading spinner component
 * Features smooth animations and attractive design
 */
const LoadingSpinner = () => {
  const { isDark } = useTheme();

  return (
    <div className={`fixed inset-0 flex justify-center items-center z-50 ${isDark
        ? 'bg-dark-primary/95 backdrop-blur-sm'
        : 'bg-white/95 backdrop-blur-sm'
      }`}>
      <div className="flex flex-col items-center space-y-6">
        {/* Animated Logo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <motion.div
            animate={{
              rotate: 360
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 opacity-20 blur-xl"
            style={{ width: '120px', height: '120px', top: '-10px', left: '-10px' }}
          />

          <motion.div
            className={`relative text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent ${isDark ? '' : 'drop-shadow-sm'
              }`}
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            PREPIFY
          </motion.div>
        </motion.div>

        {/* Animated Dots */}
        <div className="flex space-x-2">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
              animate={{
                y: [0, -10, 0],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                delay: index * 0.15,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        {/* Loading Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
        >
          Loading your experience...
        </motion.p>

        {/* Spinner Ring */}
        <div className="relative w-16 h-16">
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-600 border-r-pink-600"
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;